import { CommonModule } from '@angular/common';
import { Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SHARED_COMPONENTS } from '../../../../shared/shared-imports';
import { customValidator } from '../../../../Utils/validator';
import { SharedService } from '../../../../shared/services/shared.service';
import { AddEditPackageComponent } from './add-edit-package/add-edit-package.component';
import { ApiService } from '../../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { API_ENDPOINTS } from '../../../../core/services/api-endpoints.token';
import { CarnetQueryRequest } from '../ata-carnet/models/carnet-query.model';
import { MysteryShopperQueryRequest } from './models/mystery-shopper-query.model';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType, SrIOName } from '../../../../shared/constants/services.enum';
import { Utils } from '../../../../Utils/utils';
import { MysteryShopperCreateUpdateBody, MysteryShopperCreateUpdateRequest } from './models/mystery-shopper-create-update.model';
import { PackageQuarterQueryRequest } from './models/package-quarter-query.model';
import { SrSubmitSuccessModalComponent } from "../../../../shared/components/sr-submit-success-modal/sr-submit-success-modal.component";
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { LookupKey } from '../../../../shared/constants/lookup-keys';
import { SkeletonLoaderComponent } from "../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { FolderResponse, AllowedDocumentType } from '../../../../shared/models/documents-response.model';
import { FileComponent } from '../../../../shared/components/controls/file/file.component';
import { SrStatus } from '../../../../shared/constants/status.enum';

@Component({
  selector: 'app-mystery-shopper',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, AddEditPackageComponent, SrSubmitSuccessModalComponent, SkeletonLoaderComponent],
  templateUrl: './mystery-shopper.component.html',
  styleUrl: './mystery-shopper.component.scss'
})
export class MysteryShopperComponent implements OnDestroy {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  documentServiceType = DocumentServiceType;
  form: FormGroup;
  packageForm: FormGroup;
  lookupKey = LookupKey;
  step = 1;
  totalSteps = 3;
  isSaved = false;
  quarters: Array<{ Value: string }> = [];
  calculatedTotal = 0;
  packages: PackageDetail[] = [];
  editingPackage: any;
  showPackageModal = false;
  packagesError = false;
  showActions: boolean = true; // Added to control action visibility
  private langChangeSub?: Subscription;
  private formChangeSub?: Subscription;
  lang: string = 'en';
  loginUserDetail: any;
  userLoginName: string = '';
  userCSN: string = '';
  selectedMember: any;
  successModal: boolean = false;
  srNumber: string = "";
  srDetailLoader: boolean = false;

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.loginUserDetail = this.sharedService.getUserProfile();
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.userCSN = this.sharedService.getCsnAccountId('csn');
    this.selectedMember = this.sharedService.getPrimaryAccount();

    this.form = this.buildForm();
    this.packageForm = this.buildPackageForm();

    const routeSrNumber = this.route.snapshot.params?.['sr_no'];
    if (routeSrNumber) {
      this.srNumber = routeSrNumber;
    }

    this.sharedService.getSrNumber(this.route).subscribe(srNumber => {
      if (srNumber && srNumber !== this.srNumber) {
        this.srDetailLoader = true;
        this.srNumber = srNumber;
        this.getSRDetail();
        this.step = 1;
      }
    });
    // in case of draft
    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber;
    }

    if (!this.userCSN && !this.srNumber) {
      this.sharedService.toastMessage('INFO', 'NO_CSN_NUMBER', 'info');
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }

    if (this.srNumber) {
      this.srDetailLoader = true;
      this.getSRDetail();
      this.step = 1;
    } else {
      this.getQuarterList();
      this.setDefaults();
      this.openFolder();
    }
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      membershipNumber: [{ value: '', disabled: true }, [Validators.required]],
      memberNameAr: [{ value: '', disabled: true }, [Validators.required]],
      memberNameEn: [{ value: '', disabled: true }, [Validators.required]],
      packages: ['', [Validators.required]],
      paymentMethod: ['', [Validators.required]]
    });
  }

  private buildPackageForm(): FormGroup {
    return this.fb.group({
      packageType: [null, Validators.required],
      branches: ['', [Validators.required, customValidator('numberOnly')]],
      channels: ['', [Validators.required, customValidator('numberOnly')]],
      quartersType: [null],
      quarters: [{ value: 0, disabled: true }, [customValidator('numberOnly')]],
      unitPrice: [{ value: 0, disabled: true }, [customValidator('numberWithDecimal')]],
      vatAmount: [{ value: 0, disabled: true }, [customValidator('numberWithDecimal')]],
      totalAmount: [{ value: 0, disabled: true }, [customValidator('numberWithDecimal')]],
      totalVatAmount: [{ value: 0, disabled: true }, [customValidator('numberWithDecimal')]],
    });
  }

  private getQuarterList(): void {
    const queryReq = new PackageQuarterQueryRequest();
    queryReq.body.DCLoginName = this.userLoginName;
    queryReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    queryReq.body.DCMethod = "QUERY_PACKAGE_QUARTER";
    let portalSr = queryReq.body.DCIncomingHierarchy['ListOfDC Master Data IO']['DC Quarter List BC'];
    portalSr['CSN'] = this.userCSN;
    portalSr['SR Type'] = ServiceName.SERVICE_EXCELLENCE_PROGRAMME;
    portalSr['Sub Type'] = ServiceName.MYSTERY_SHOPPER_MEMBERSHIP

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.QUERY_PACKAGE_QUARTER,
      queryReq
    ).subscribe({
      next: (response: any) => {
        if (response['Error Code'] == null && response?.SiebelMessage && response?.SiebelMessage["DC Quarter List BC"]) {
          this.quarters = Utils.checkIsArray(response?.SiebelMessage["DC Quarter List BC"]);
        }
      }
    });
  }

  private setDefaults(): void {
    const patch: any = {};
    this.form.patchValue({
      membershipNumber: this.userCSN,
      memberNameAr: this.selectedMember['DC Name Arabic'],
      memberNameEn: this.selectedMember['DC Name English'],
      ...patch
    });
    this.getPackagesLOV();
  }

  // This method is used to open add/edit package detail
  openPackageModal(index: any | null = null): void {
    this.editingPackage = index;
    this.packageForm.reset();
    const defaultValues = {
      quarters: this.quarters[0]?.Value || null,
    };
    if (index !== null) {
      this.packageForm.patchValue(this.editingPackage);
    } else {
      this.packageForm.patchValue(defaultValues);
    }
    this.showPackageModal = true;
  }

  // This method is used to open package modal
  closePackageModal(): void {
    this.packageForm.reset();
    this.editingPackage = null;
    this.showPackageModal = false;
  }
  // This method is use add/update package
  savePackage(): void {
    if (this.packageForm.invalid) {
      this.packageForm.markAllAsTouched();
      return;
    }

    const pkg: PackageDetail = this.packageForm.getRawValue();

    if (this.editingPackage !== null) {
      // Find index using packageId
      const index = this.packages.findIndex(
        p => p.packageId === this.editingPackage!.packageId
      );

      if (index !== -1) {
        // Preserve packageId on update
        pkg.packageId = this.packages[index].packageId;
        this.packages[index] = pkg;
      }
    } else {
      // New package
      pkg.packageId = Utils.generateUniqueId();
      this.packages.push(pkg);
    }
    this.packagesError = false;
    this.closePackageModal();

    // Update pagination
    this.totalPages = Math.ceil(this.packages.length / this.itemsPerPage);
    this.form.get("packages")?.setValue(this.packages.length > 0 ? this.packages : "");
    if (this.totalPages < 2) {
      this.currentPage = 1;
    }
    this.loadPage(this.currentPage);
  }

  calculateRowTotal(pkg: PackageDetail): number {
    const toNumber = (val: any) => Number(val || 0);
    const base = toNumber(pkg.unitPrice) * toNumber(pkg.quarters);
    return Math.max(0, Math.round(base + toNumber(pkg.vatAmount)));
  }

  // This method is used to remove package
  removePackage(packageToRemove: any): void {
    this.packages = this.packages.filter((p: any) => p.packageId !== packageToRemove.packageId);

    // Recalculate total pages
    this.totalPages = Math.ceil(this.packages.length / this.itemsPerPage);

    // If current page is now empty, go back one page (if not on page 1)
    const currentStartIndex = (this.currentPage - 1) * this.itemsPerPage;
    if (this.packages.length <= currentStartIndex && this.currentPage > 1) {
      this.currentPage--;
    }
    this.form.get("packages")?.setValue(this.packages ? this.packages : "");
    this.loadPage(this.currentPage);
  }

  // Displayed subset of products
  visibleProducts: any[] = [];
  currentPage: number = 1;
  itemsPerPage: number = 4;
  totalPages: number = 0;
  // This method is used to load page using page
  loadPage(page: number): void {
    this.currentPage = page;
    const start = (page - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.visibleProducts = this.packages.slice(start, end);    
  }


  // This method is used to move to the next step
  nextStep(): void {
    let fields = [
      'membershipNumber',
      'memberNameAr',
      'memberNameEn',
      'packages'
    ]
    const stepOneFields =
      this.step === 1
        ? fields
        : [];

    const stepThreeFields =
      this.step === 3
        ? ['paymentMethod']
        : [];

    if (
      stepOneFields.length > 0 &&
      stepOneFields.some((f) => this.form.get(f)?.invalid)
    ) {
      stepOneFields.forEach((f) => this.form.get(f)?.markAsTouched());
      return;
    }

    if (
      stepThreeFields.length > 0 &&
      stepThreeFields.some((f) => this.form.get(f)?.invalid)
    ) {
      stepThreeFields.forEach((f) => this.form.get(f)?.markAsTouched());
      const invalidControls = Utils.getInvalidControls(this.form);
      this.form.markAllAsTouched();
      console.warn('Invalid controls:', invalidControls);
      this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }
    if (this.step <= 2) {
      if (!this.srNumber) {
        this.loadingDoc = true;
      }
      this.submitSRData(this.form.getRawValue(), SrActionType.DRAFT);
    }
    if (this.step >= 3) {
      this.submitSRData(this.form.getRawValue(), SrActionType.SUBMIT);
    }
    if (this.step < 3) {
      this.step++;
    }
  }

  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;
  // This method is used to get sr detail
  getSRDetail() {
    if (!this.srNumber) {
      return;
    }
    this.noAdvancePaymentBalance = false;
    const queryReq = new MysteryShopperQueryRequest();
    queryReq.body.DCLoginName = this.userLoginName;
    queryReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    queryReq.body.DCMethod = "QUERY";
    let portalSr = queryReq.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR']
    portalSr['CSN'] = this.userCSN || "";
    portalSr['SR Number'] = this.srNumber;
    portalSr['SR Type'] = ServiceName.SERVICE_EXCELLENCE_PROGRAMME;
    portalSr['Sub Type'] = ServiceName.MYSTERY_SHOPPER_MEMBERSHIP

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
      queryReq
    ).subscribe({
      next: (response: any) => {
        if (response["Error Code"] == null && response?.SiebelMessage["DC Community Portal SR"]) {
          let serviceRequest = response?.SiebelMessage["DC Community Portal SR"];
          this.userCSN = serviceRequest['DC Member Num'] || this.userCSN;
          if (!this.quarters?.length) {
            this.getQuarterList();
          }
          this.srDetailLoader = false;
          this.form.patchValue({
            membershipNumber: serviceRequest['DC Member Num'],
            memberNameEn: serviceRequest['DC Member Name ENU'],
            memberNameAr: serviceRequest['DC Member Name ARA'],
            paymentMethod: serviceRequest['DC Payment Method']
          })
          //packages list
          if (serviceRequest['DC Community Portal Mem Pack Details']) {
            let packList = Utils.checkIsArray(serviceRequest['DC Community Portal Mem Pack Details']);
            packList?.map((pkg, index) => {
              let quarterList: any = [];
              let quarters = Utils.checkIsArray(pkg["DC Community Portal Mem Pack Details_QuarterMvl"]);
              if (quarters) {
                quarters.map((q: any) => {
                  quarterList.push(q["DC Quarter"])
                })
              }
              let pkgObj: PackageDetail = {
                packageId: String(index + 1),
                packageType: pkg['DC Package Type'],
                branches: pkg['DC No of Branch'],
                channels: pkg['DC No of eChannels'],
                quartersType: quarterList ? quarterList : "",
                quarters: quarterList.length,
                unitPrice: pkg['DC Unit Price'],
                vatAmount: pkg['DC VAT Amt'],
                totalAmount: pkg['DC Total Amt'],
                totalVatAmount: pkg['DC Total VAT Amt']
              }
              this.packages.push(pkgObj);
            })
            if (this.packages) {
              this.form.get('packages')?.setValue(this.packages);
              this.loadPage(1);
            }
          }

          if (this.sharedService.enableNotesOption(serviceRequest["Status"])) {
            this.srInprgoressAwating = true;
            this.showNotes = true;
          }
          if (serviceRequest["Status"] != SrStatus.Draft && serviceRequest["Status"] != SrStatus.AwaitingClarification) {
            this.showActions = false;
            this.form.disable(); // disables all fields in the form
          }
          this.step = 3;
          this.openFolder();
          this.srDetailLoader = false;
        } else {
          this.srDetailLoader = false;
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      }
    })

  }
  // This method is used to save and close
  saveAndClose(): void {
    this.submitSRData(this.form.getRawValue(), SrActionType.SAVE);
  }

  errorMsg: any = "";
  formSubmitLoader: boolean = false;
  technicalErrorMsg: boolean = false;
  submitSRData(formData: any, actionType: string) {
    this.errorMsg = "";
    let isActionSubmit = actionType == SrActionType.SUBMIT;
    let isActionSave = actionType == SrActionType.SAVE;
    let isActionDraft = actionType == SrActionType.DRAFT;
    const defaultShowActions = this.showActions;
    const restoreFormState = () => {
      this.showActions = defaultShowActions;
      this.form.enable({ emitEvent: false });
      this.form.get('membershipNumber')?.disable({ emitEvent: false });
      this.form.get('memberNameAr')?.disable({ emitEvent: false });
      this.form.get('memberNameEn')?.disable({ emitEvent: false });
      this.formSubmitLoader = false;
    };

    if (this.packages.length < 1) {
      this.packagesError = true;
      return;
    }
    const requestPayload = new MysteryShopperCreateUpdateRequest();

    requestPayload.body.DCLoginName = this.userLoginName;
    requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    requestPayload.body.DCMethod = isActionSubmit ? 'SUBMIT' : 'SAVEASDRAFT';
    let portalSr = requestPayload.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"];
    portalSr["SR Number"] = this.srNumber;
    portalSr.CSN = this.userCSN;
    portalSr['SR Type'] = ServiceName.SERVICE_EXCELLENCE_PROGRAMME;
    portalSr['Sub Type'] = ServiceName.MYSTERY_SHOPPER_MEMBERSHIP;
    portalSr["DC Payment Method"] = formData.paymentMethod ? formData.paymentMethod : "";
    portalSr["DC Channel"] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;

    let packList: any = [];
    if (this.packages) {
      this.packages.map((pkg: PackageDetail) => {
        let qrTypes: any = [];
        pkg?.quartersType?.map((qt: any) => {
          qrTypes.push({ "DC Quarter": qt })
        })
        let packageObj = {
          "DC Package Type": pkg.packageType,
          "DC No of Branch": pkg.branches,
          "DC No of eChannels": pkg.channels,
          "DC Unit Price": pkg.unitPrice,
          "DC VAT Amt": pkg.vatAmount,
          "DC Total Amt": pkg.totalAmount,
          "DC Total VAT Amt": pkg.totalVatAmount,
          "ListOfDC Community Portal Mem Pack Details_QuarterMvl": {
            "DC Community Portal Mem Pack Details_QuarterMvl": qrTypes ? qrTypes : null
          }
        }
        packList.push(packageObj)
      })
    }
    portalSr["ListOfDC Community Portal Mem Pack Details"]['DC Community Portal Mem Pack Details'] = packList;
    if (isActionSubmit) {
      if (this.checkRequiredDocuments() || this.noAdvancePaymentBalance || this.loadingDoc) {
        this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
        return;
      }
    }
    if (!isActionDraft) {
      this.showActions = false;
      this.form.disable({ emitEvent: false });
      this.formSubmitLoader = true;
    }
    this.technicalErrorMsg = false;
    this.packagesError = false;
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["DCSRNumber"] && response["Error Message"] == null || response["Error Message"] == "") {
          this.isSaved = true;
          if (isActionSave || isActionDraft) {
            if (isActionDraft && this.srNumber == "") {
              this.srNumber = response["DCSRNumber"];
              sessionStorage.setItem("srNumber", response["DCSRNumber"]);
              this.openFolder();
            } else {
              this.srNumber = response["DCSRNumber"];
              if (isActionSave) {
                this.successModal = true;
              }
            }
          } else {
            this.srNumber = response["DCSRNumber"];
            let lang: any = this.sharedService.getCurrentLang();

            const { service, serviceAr } = Utils.getServiceLabels(ServiceName.MYSTERY_SHOPPER_MEMBERSHIP, lang);
            let srDetailObj = {
              serviceSubTypeEn: service,
              serviceSubTypeAr: serviceAr,
              service: ServiceName.SERVICE_EXCELLENCE_PROGRAMME,
              IOName: SrIOName.ISSUE_NEW_ATA_CARNET,
              srNumber: this.srNumber,
              totalSteps: this.totalSteps,
              step: this.step + 1
            }
            this.sharedService.setSrResponse(srDetailObj);

            this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
            sessionStorage.removeItem("srNumber");
            if (response['DCSRStatus'] == SrStatus.ApprovedAndPaymentAwaited &&
              (formData.paymentMethod == FieldValue.PAYMENT_METHOD_ONLINE_EN ||
                formData.paymentMethod == FieldValue.PAYMENT_METHOD_ONLINE_AR)) {
              this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${response["DCSRNumber"]}`]);

            } else {
              this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
            }
          }
          restoreFormState();
        } else {
          restoreFormState();
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != "") {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },
      error: () => {
        restoreFormState();
        this.technicalErrorMsg = true;
      }
    });
  }

  get formProgress() {
    if (this.form.disabled) {
      return 100;
    }
    if (!this.form.invalid) {
      if (!this.checkRequiredDocuments()) {
        return 100;
      }
    }
    return Utils.calculateFormProgress(this.step, this.totalSteps);
  }

  // This method is used to close success modal
  closeSuccessModal() {
    this.successModal = false;
    sessionStorage.removeItem("srNumber");
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  //get folder id and attachments
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];
  visibleDocumentTypes: AllowedDocumentType[] = [];
  loadingDoc: boolean = false
  // This method is used to open folder
  openFolder() {
    this.loadingDoc = true;
    let isLabels = !this.srNumber; // true if srNumber is empty
    this.sharedService.getDocuments(this.srNumber, DocumentServiceType.PS, isLabels)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.loadingDoc = false;
            this.srFolder = response.data ? response.data : response;
          } else {
            this.loadingDoc = false;
            this.sharedService.toastMessage("error", response.message, 'error');
          }
        },
        error: err => {
          this.loadingDoc = false;
          this.sharedService.toastMessage("error", err, 'error');
        }
      });
  }

  // This method is used to handle payment method change event using option
  noAdvancePaymentBalance: boolean = false;
  onPaymentMethodChange(value: any) {
    this.noAdvancePaymentBalance = false;
    const selectedValue = value;
    if (selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES || selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES_AR) {
      this.noAdvancePaymentBalance = !this.sharedService.hasSufficientAdvanceForServicesBalance();
    } else {
      this.noAdvancePaymentBalance = false;
    }
  }

  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }

    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      if (!cmp) continue;

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }
  packagesList: any = [];
  getPackagesLOV() {
    let request = {
      body: {
        ProcessName: "DC Get List Of Values WF",
        Type: LookupKey.DC_MEM_PACKAGE_TYPE,
        "LanguageCode": this.sharedService.getCurrentLang() == "ar" ? 'ARA' : "ENU",
        ParentValue: "",
        "Sub Type": ""
      }
    }
    this.api
      .post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      .subscribe({
        next: (result: any) => {
          if (result.SiebelMessage?.['List Of Values'] && result["Error Message"] == null || result["Error Message"] == "") {
            this.packagesList = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
          }
        }
      })
  }
  canDeactivate(): boolean {
    return this.sharedService.confirmNavigationIfFormDirty(this.form, this.isSaved);
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem("srNumber");
    this.langChangeSub?.unsubscribe();
    this.formChangeSub?.unsubscribe();
  }
}

interface PackageDetail {
  packageId: string;
  packageType: string;
  branches: string;
  channels: string;
  quartersType: any;
  quarters: string;
  unitPrice: string;
  vatAmount: string;
  totalAmount: string;
  totalVatAmount: string;
}
